import React, { FC, useState } from 'react';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { enableEInvoicingTemplate, labEInvoicingTemplate } from 'apollo/templates/e-invoicing';
import { LabEInvoicing } from 'models/e-invoicing';
import { formatDate } from 'utils/format/format-date';

/** What the lab still has to fix, one line per E_INVOICING_* key from the backend. */
const RequirementList: FC<{ requirements: string[] }> = ({ requirements }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    return (
        <Box component="ul" sx={{ my: 0, pl: 2.5 }} data-test="e-invoicing.requirements">
            {requirements.map((requirement) => (
                <li key={requirement}>
                    <Typography variant="body2">{translate(`lab-settings:${requirement}`)}</Typography>
                </li>
            ))}
        </Box>
    );
};

/**
 * DD-3726: the "Electronic invoices" section of the lab's invoice settings. A lab admin
 * enables e-invoicing once; it cannot be turned off, as the delivery method per customer
 * decides what is sent.
 */
const EInvoicingSettings: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const { data, loading, error } = useQuery<{ labEInvoicing: LabEInvoicing }>(labEInvoicingTemplate, {
        fetchPolicy: 'network-only',
    });
    const [enableEInvoicing, { loading: enabling }] = useMutation(enableEInvoicingTemplate, {
        refetchQueries: [labEInvoicingTemplate],
        onCompleted: () => setIsConfirmOpen(false),
        onError: (mutationError) => {
            setIsConfirmOpen(false);
            showBoundary(mutationError);
        },
    });

    const status = data?.labEInvoicing;

    const renderBody = () => {
        if (loading) {
            return <Skeleton variant="rectangular" height={64} />;
        }
        if (error || !status) {
            return <Alert severity="error">{translate('lab-settings:E_INVOICING_LOAD_FAILED')}</Alert>;
        }
        if (status.enabled) {
            return (
                <Box display="flex" flexDirection="column" gap={1.5}>
                    <Alert severity="success" data-test="e-invoicing.enabled">
                        {status.enabledAt
                            ? translate('lab-settings:E_INVOICING_ENABLED_SINCE', {
                                  date: formatDate(status.enabledAt, 'date'),
                                  // React escapes the output; i18next would escape the slashes again.
                                  interpolation: { escapeValue: false },
                              })
                            : translate('lab-settings:E_INVOICING_ENABLED')}
                    </Alert>
                    {status.requirements.length > 0 && (
                        <Alert severity="warning" data-test="e-invoicing.enabled-requirements">
                            <Typography variant="body2" mb={0.5}>
                                {translate('lab-settings:E_INVOICING_FIX_TO_KEEP_SENDING')}
                            </Typography>
                            <RequirementList requirements={status.requirements} />
                        </Alert>
                    )}
                </Box>
            );
        }
        return (
            <Box display="flex" flexDirection="column" gap={1.5} alignItems="flex-start">
                <Typography variant="body1">{translate('lab-settings:E_INVOICING_NOT_ENABLED')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('lab-settings:E_INVOICING_DESCRIPTION')}
                </Typography>
                {status.requirements.length > 0 && (
                    <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
                        <Typography variant="body2" mb={0.5}>
                            {translate('lab-settings:E_INVOICING_FIX_TO_ENABLE')}
                        </Typography>
                        <RequirementList requirements={status.requirements} />
                    </Alert>
                )}
                {status.canEnable ? (
                    <Button
                        variant="contained"
                        disabled={status.requirements.length > 0}
                        onClick={() => setIsConfirmOpen(true)}
                        data-test="e-invoicing.enable"
                    >
                        {translate('lab-settings:E_INVOICING_ENABLE')}
                    </Button>
                ) : (
                    <Typography variant="body2" color="text.secondary" data-test="e-invoicing.admin-only">
                        {translate('lab-settings:E_INVOICING_ADMIN_ONLY')}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box data-test="e-invoicing-settings">
            <PageTitle headingStyle="h3" title={translate('lab-settings:E_INVOICING_TITLE')} />
            {renderBody()}
            <DHDialog
                open={isConfirmOpen}
                size="sm"
                fullWidth
                onClose={() => setIsConfirmOpen(false)}
                title={translate('lab-settings:E_INVOICING_CONFIRM_TITLE')}
                actions={
                    <>
                        <Button onClick={() => setIsConfirmOpen(false)} disabled={enabling}>
                            {translate('common:CANCEL')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => enableEInvoicing()}
                            disabled={enabling}
                            data-test="e-invoicing.confirm"
                        >
                            {translate('lab-settings:E_INVOICING_ENABLE')}
                        </Button>
                    </>
                }
            >
                <Typography variant="body2">{translate('lab-settings:E_INVOICING_CONFIRM_MESSAGE')}</Typography>
            </DHDialog>
        </Box>
    );
};

export default EInvoicingSettings;
